import React from "react";
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { LocationStatus } from "../../../../types/LocationStatus";

interface LocationTabBadgeProps {
  variant: LocationStatus;
  count: number;
  isTabActive: boolean;
}

/**
 * Badge color tokens derived from the mockup's `.tab-badge.*` CSS rules.
 * Each variant has an inactive (muted) and active (saturated) state.
 */
const BADGE_STYLES: Record<LocationStatus, {
  inactive: { bg: string; color: string };
  active:   { bg: string; color: string };
}> = {
  active:      { inactive: { bg: "#e6f9ee", color: "#2a7d4a" }, active: { bg: "#c4f0d4", color: "#1a6636" } },
  pending:     { inactive: { bg: "#fff3cc", color: "#8a6400" }, active: { bg: "#ffe9a0", color: "#6a4800" } },
  draft:       { inactive: { bg: "#e8edff", color: "#3f4fc1" }, active: { bg: "#c8d4ff", color: "#2a3faa" } },
  deactivated: { inactive: { bg: "#fde8e8", color: "#a82020" }, active: { bg: "#fbbaba", color: "#8a1010" } },
};

const BadgeRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== "ownerVariant" && prop !== "ownerActive",
})<{ ownerVariant: LocationStatus; ownerActive: boolean }>(
  ({ ownerVariant, ownerActive }) => {
    const { bg, color } = ownerActive
      ? BADGE_STYLES[ownerVariant].active
      : BADGE_STYLES[ownerVariant].inactive;
    return {
      backgroundColor: bg,
      color,
      borderRadius: "20px",
      fontSize: "0.7857rem",   // 11px at 14px root
      fontWeight: 600,
      padding: "1px 8px",
      lineHeight: 1.6,
      flexShrink: 0,
    };
  }
);

/**
 * Pill-shaped count badge for a location list tab.
 * Color shifts from muted to saturated when the parent tab is active.
 */
const LocationTabBadge = ({ variant, count, isTabActive }: LocationTabBadgeProps) => (
  <BadgeRoot ownerVariant={variant} ownerActive={isTabActive}>
    {count}
  </BadgeRoot>
);

export default LocationTabBadge;

